import { useMemo, useState } from "react";
import { InfoList } from "@landtechnologies/components";
import { RecordType, useZooplaComparableByUPRN } from "react-migration/domains/comparables";
import { LandTechSection } from "react-migration/domains/comparables/components/Results/Record/LandTechSection";
import { ZooplaSection } from "react-migration/domains/comparables/components/Results/Record/ZooplaSection";
import {
  formatZooplaData,
  marketValue,
} from "react-migration/domains/comparables/components/Results/Record/helpers/formatZooplaData";
import { squareMetersToSquareFeet, useTranslation } from "react-migration/lib";
import { Feature } from "src/js/stores/user";
import { hasFeature } from "src/js/stores/user/actions";
import { MeasurementSystem, userStore } from "src/js/stores/user/store";
import { SupportedLocale } from "src/js/util/dates";

type ZooplaSearchRecordItemProps = {
  record: RecordType;
  unitPreference: MeasurementSystem;
};

export const ZooplaSearchRecordItem = ({ record, unitPreference }: ZooplaSearchRecordItemProps) => {
  const { t } = useTranslation();
  const locale = userStore.locale as SupportedLocale;
  const [isOpen, setOpen] = useState<boolean>(false);

  const { loading: zooplaDataLoading, data: zooplaData } = useZooplaComparableByUPRN({
    uprn: record.uprn,
    skip: !hasFeature(Feature.zooplaFullAccess),
  });

  const formattedZooplaData = useMemo(
    () => formatZooplaData({ data: zooplaData, locale, unitPreference, t }),
    [zooplaData, locale, t, unitPreference]
  );

  return (
    <InfoList.Item
      className="atlas-cursor-pointer !atlas-px-0 !atlas-py-0"
      data-testid="comparables-overview-search-record-zoopla-item"
    >
      <div
        className="hover:atlas-bg-background-dark atlas-py-2"
        onClick={() => setOpen(!isOpen)}
        data-testid="comparables-overview-search-record-item-trigger"
      >
        {!isOpen ? (
          <div className="atlas-flex atlas-flex-col">
            <div className="atlas-flex atlas-w-full atlas-justify-between atlas-items-center atlas-gap-x-2">
              <span className="atlas-flex atlas-font-semibold">{record.address}</span>
              <i className="atlas-flex icon-lt-arrow-down-s-line atlas-text-md" />
            </div>
            <span className="atlas-flex">
              {marketValue({
                t,
                locale,
                unitPreference,
                floorArea: squareMetersToSquareFeet(record.total_floor_area ?? 0),
                currentValue: record.price,
              })}
            </span>
          </div>
        ) : (
          <div className="atlas-flex atlas-py-2 atlas-justify-between atlas-items-center">
            <div className="atlas-flex atlas-flex-col atlas-text-md atlas-font-semibold">
              {record.address.split(",").map((addressPart) => (
                <span key={addressPart}>{addressPart}</span>
              ))}
            </div>
            <i className="atlas-flex icon-lt-arrow-up-s-line atlas-text-md" />
          </div>
        )}
      </div>
      {isOpen && (
        <div className={!formattedZooplaData.length ? "-atlas-mx-2" : "-atlas-ml-3"}>
          <ZooplaSection
            data={formattedZooplaData}
            loading={zooplaDataLoading}
            showDivider={false}
          />
          <div className="atlas-border-b atlas-border-border-divider-subtle atlas-ml-3" />
          <LandTechSection record={record} unitPreference={unitPreference} locale={locale} />
        </div>
      )}
    </InfoList.Item>
  );
};
